import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectAccessorySizeById } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { actions as generalUiActions } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import { selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { selectAllPhotos, actions as photosActions } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { selectVariantByAttributes } from '../_redux-variants';
import { applyFirstMagicBorderIfFotofun } from '../helpers/applyFirstMagicBorderIfFotofun';
import { addGroup } from './addGroup';

export interface ApplyAccessoryPayload {
    groupId: string;
    accessoryId: string;
    variantId?: string;
    sizeId?: string;
    selectedPhotos?: string[];
    borderId?: string;
}

export const applyAccessoryThunk = createAsyncThunk<
    void,
    ApplyAccessoryPayload,
    { state: PhotoPrintsPdpState }
>(
    'photos/applyAccessory',
    async ({ groupId, accessoryId, sizeId, selectedPhotos, borderId = "without-border" }, { dispatch, getState }) => {

        const state = getState();

        const originalGroup = selectGroupById(state, groupId);
        const accessorySize = selectAccessorySizeById(state, accessoryId);
        const variant =  selectVariantByAttributes(state, "accessory", accessoryId, borderId);

        let currentPhotos = selectAllPhotos(state).filter(photo => photo.groupId === groupId);

        if (selectedPhotos && selectedPhotos.length > 0) {
            const selectedIds = new Set(selectedPhotos);
            currentPhotos = currentPhotos.filter(photo => selectedIds.has(photo.id));
        }

        if (!accessorySize) {
            throw new Error('Accessory not found');
        }

        if (!originalGroup) {
            throw new Error('Original group not found');
        }

        if (!currentPhotos) {
            throw new Error('Photos not found');
        }

        const newGroupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newVariantId = variant?.id!;
        
        // Only preserve borders if the new variant supports them (contains "fotofun")
        const shouldPreserveBorders = newVariantId.includes('fotofun') && 
            (originalGroup.magicBorderId || originalGroup.patternId);
        
        const newGroup: PhotoPrintsJson.Group = {
            id: newGroupId,
            variantId: newVariantId,
            amountInQueue: 0,
            // Conditionally preserve the original group's border settings
            magicBorderId: shouldPreserveBorders ? originalGroup.magicBorderId : undefined,
            magicBorderAssetId: shouldPreserveBorders ? originalGroup.magicBorderAssetId : undefined,
            patternId: shouldPreserveBorders ? originalGroup.patternId : undefined
        };

        const newPhotos: PhotoPrintsJson.Photo[] = currentPhotos.map(originalPhoto => {

            const newPhotoId = `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            if (!originalPhoto.processed) {
                const UnprocessedPhoto: PhotoPrintsJson.UnprocessedPhoto = {
                    ...originalPhoto,
                    id: newPhotoId,
                    groupId: newGroupId
                };
                return UnprocessedPhoto;
            }

            const newPhoto: PhotoPrintsJson.ProcessedPhoto = {
                ...originalPhoto,
                id: newPhotoId,
                groupId: newGroupId,
                accessoryId: accessoryId,
                accessorySizeId: sizeId || 'default'
            };

            return newPhoto;
        });

        newPhotos.forEach(photo => {
            dispatch(photosActions.add(photo));
        });

        dispatch(addGroup(newGroup));

        // Handle border preservation or default application based on variant support
        if (shouldPreserveBorders) {
            // Preserve UI state for magic borders if we preserved them
            const borderId = originalGroup.magicBorderId || originalGroup.patternId;
            if (borderId) {
                dispatch(generalUiActions.updateMagicBorderForGroup({
                    groupId: newGroupId,
                    magicBorderId: borderId
                }));
            }
        } else if (newVariantId.includes('fotofun')) {
            // Only apply default magic border if:
            // 1. The variant contains "fotofun"
            // 2. The original group didn't have any border applied OR the border wasn't preserved
            dispatch(applyFirstMagicBorderIfFotofun(newGroupId, newVariantId));
        }
    }
);